import { toast } from 'sonner';
import React, { useState } from 'react';
import Select, { MultiValue } from 'react-select';
import {
  FormControl,
  FormItem,
  FormLabel,
  FormField,
  Form,
  FormMessage,
} from '@/components/ui/form';
import { useGetCNCBrandsQuery } from '@/redux/cncBrand/cncBrand.api.slice';
import { useLazyGetCNCModelsQuery } from '@/redux/cncModel/cncModel.api.slice';
import { CNCBrandI } from '@/redux/cncBrand/cncBrand';
import { useAddMachineForm } from '../useAuthForm';
import { DialogClose, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useAddMachineMutation } from '../auth.api.slice';
import { useLazyGetCNCVariantsQuery } from '@/redux/cncVariant/cncVariant.api.slice';

function AddMachineForm({ onSucess }: { onSucess: () => void }) {
  const { data: cncBrandsData, isLoading: isLoadingCNCBrands } =
    useGetCNCBrandsQuery();
  const [getCNCModels, { isLoading: isLoadingCNCModels }] =
    useLazyGetCNCModelsQuery();
  const [getCNCVariants, { isLoading: isLoadingCNCVariants }] =
    useLazyGetCNCVariantsQuery();

  const [cncModelOptions, setCncModelOptions] = useState<
    {
      label: string;
      value: string;
    }[]
  >([]);

  const [cncVariantOptions, setCncVariantOptions] = useState<
    {
      label: string;
      value: string;
    }[]
  >([]);

  const [ownedMachines, setOwnedMachines] = useState<{
    cncBrand: string[];
    cncModel: string[];
    cncVariant: string[];
  }>({
    cncBrand: [],
    cncModel: [],
    cncVariant: [],
  });

  const addMachineForm = useAddMachineForm();

  const [addMachine, { isLoading: isLoadingAddMachine }] =
    useAddMachineMutation();

  const getOptionsFromCncBrands = (cncBrands: CNCBrandI[] | undefined) => {
    if (!cncBrands) return [];
    const options = cncBrands.map((brand) => ({
      label: brand.name,
      value: brand._id,
    }));
    return options;
  };

  const handleCncBrandChange = (
    newValue: MultiValue<{ label: string; value: string }>
  ) => {
    const cncBrands = newValue.map((brand) => brand.value);

    setOwnedMachines((prev) => ({
      ...prev,
      cncBrand: cncBrands,
      cncModel: [],
      cncVariant: [],
    }));
  };

  const handleCncModelChange = (
    option: MultiValue<{ label: string; value: string } | null>
  ) => {
    setOwnedMachines((prev) => ({
      ...prev,
      cncModel: option
        .map((model) => model?.value)
        .filter((value): value is string => !!value),
      cncVariant: [],
    }));
  };

  const handleCncVariantChange = (
    option: MultiValue<{ label: string; value: string } | null>
  ) => {
    setOwnedMachines((prev) => ({
      ...prev,
      cncVariant: option
        .map((variant) => variant?.value)
        .filter((value): value is string => !!value),
    }));
  };

  const getOptionsFromCncModels = async () => {
    const cncModels = await getCNCModels(ownedMachines.cncBrand).unwrap();
    const options = cncModels.map((model) => ({
      label: model.name,
      value: model._id,
    }));
    setCncModelOptions(options);
  };

  const getOptionsFromCncVariants = async () => {
    const cncVariants = await getCNCVariants({
      modelIdList: ownedMachines.cncModel,
    }).unwrap();

    const options = cncVariants.map((variant) => ({
      label: variant.name,
      value: variant._id,
    }));
    setCncVariantOptions(options);
  };

  const handleAddMachine = async (e: React.MouseEvent<HTMLButtonElement>) => {
    try {
      e.preventDefault();
      await addMachine({
        cncVariantIdList: ownedMachines.cncVariant,
      }).unwrap();
      toast.success('Machine(s) added successfully');
      onSucess();
    } catch (error) {
      toast.error('Failed to add machine(s)');
    }
  };

  return (
    <Form {...addMachineForm}>
      <form className="space-y-6">
        <div className="flex flex-col py-2 gap-4">
          <div className="flex flex-col gap-2">
            <FormField
              control={addMachineForm.control}
              name="cncBrand"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>CNC Brand</FormLabel>
                  <FormControl>
                    <Select
                      options={getOptionsFromCncBrands(cncBrandsData)}
                      onChange={(newValue) => handleCncBrandChange(newValue)}
                      isLoading={isLoadingCNCBrands}
                      isSearchable
                      isMulti
                      name={field.name}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          <div className="flex flex-col gap-2">
            <FormField
              control={addMachineForm.control}
              name="cncModel"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>CNC Model</FormLabel>
                  <FormControl>
                    <Select
                      options={cncModelOptions}
                      onChange={(option) => handleCncModelChange(option)}
                      isLoading={isLoadingCNCModels}
                      isSearchable
                      onFocus={() => {
                        getOptionsFromCncModels();
                      }}
                      value={ownedMachines?.cncModel
                        .map((modelId) => {
                          const model = cncModelOptions?.find(
                            (m) => m.value === modelId
                          );
                          return model
                            ? { label: model.label, value: model.value }
                            : null;
                        })
                        .filter(Boolean)}
                      name={field.name}
                      isMulti
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          <div className="flex flex-col gap-2">
            <FormField
              control={addMachineForm.control}
              name="cncVariant"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>CNC Variant</FormLabel>
                  <FormControl>
                    <Select
                      options={cncVariantOptions}
                      onChange={(option) => handleCncVariantChange(option)}
                      isLoading={isLoadingCNCVariants}
                      isSearchable
                      onFocus={() => {
                        getOptionsFromCncVariants();
                      }}
                      value={ownedMachines?.cncVariant
                        .map((variantId) => {
                          const variant = cncVariantOptions?.find(
                            (v) => v.value === variantId
                          );
                          return variant
                            ? { label: variant.label, value: variant.value }
                            : null;
                        })
                        .filter(Boolean)}
                      name={field.name}
                      isMulti
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </div>
        <DialogFooter className="flex justify-end gap-2">
          <DialogClose asChild>
            <Button type="button" variant="outline">
              Cancel
            </Button>
          </DialogClose>
          <Button
            type="submit"
            onClick={handleAddMachine}
            disabled={isLoadingAddMachine}
          >
            Add Machine
          </Button>
        </DialogFooter>
      </form>
    </Form>
  );
}

export default AddMachineForm;
